import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ConfirmationService, MessageService, PrimeNGConfig } from 'primeng/api';
import { BreadcrumbService } from 'src/app/app.breadcrumb.service';
import { AbstractService } from 'src/app/services/abstract.service';
import { BlogService, CategoriaBlog, GrupoMudanca, PostBlog, REACOES_BLOG } from 'src/app/services/blog.service';
import { registrarAviso, TIPOS_DE_AVISO } from './quill-aviso';
import { enderecoDoVideo, textoParaHtml, textoVazio } from './texto-rico';

type Status = 'rascunho' | 'agendado' | 'publicado';

interface Formulario {
  titulo: string;
  categoria: CategoriaBlog;
  versao: string;
  publicarEm: Date;
  resumo: string;
  texto: string;
  novo: string;
  melhoria: string;
  correcao: string;
  destaque: boolean;
  enquetePergunta: string;
  enqueteOpcoes: string;
}

export const CATEGORIAS_BLOG: { value: CategoriaBlog; label: string; icon: string }[] = [
  { value: 'novidade', label: 'Novidade', icon: 'pi pi-star' },
  { value: 'correcao', label: 'Correção', icon: 'pi pi-check-circle' },
  { value: 'empreendedorismo', label: 'Empreendedorismo', icon: 'pi pi-chart-line' },
  { value: 'aviso', label: 'Aviso', icon: 'pi pi-volume-up' },
  { value: 'dica', label: 'Dica', icon: 'pi pi-info-circle' },
];

const COM_MUDANCAS: CategoriaBlog[] = ['novidade', 'correcao'];

@Component({
  selector: 'habil-blog-haila',
  templateUrl: './blog-haila.component.html',
  styleUrls: ['./blog-haila.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class BlogHailaComponent implements OnInit {
  readonly categorias = CATEGORIAS_BLOG;
  readonly reacoes = REACOES_BLOG;
  readonly tiposDeAviso = TIPOS_DE_AVISO;
  videoAberto = false;
  linkDoVideo = '';
  enviandoImagem = false;
  private quill: any = null;
  private posicaoDoVideo = 0;
  readonly grupos: { chave: GrupoMudanca; label: string }[] = [
    { chave: 'novo', label: 'O que chegou' },
    { chave: 'melhoria', label: 'Melhorias' },
    { chave: 'correcao', label: 'Correções' },
  ];

  carregando = false;
  salvando = false;
  posts: PostBlog[] = [];
  busca = '';
  raizAberta = true;
  pastasFechadas = new Set<CategoriaBlog>();
  readonly plurais: Record<CategoriaBlog, string> = {
    novidade: 'Novidades',
    correcao: 'Correções',
    empreendedorismo: 'Empreendedorismo',
    aviso: 'Avisos',
    dica: 'Dicas',
  };

  editando: PostBlog = null;
  novo = false;
  form: Formulario = null;
  private original = '';

  imagemHaila: string;

  constructor(
    private service: BlogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private config: PrimeNGConfig,
    breadcrumbService: BreadcrumbService,
    titleService: Title
  ) {
    breadcrumbService.setItems([{ label: 'News do Hábil' }]);
    titleService.setTitle('News do Hábil - ' + AbstractService.app);
    this.imagemHaila = service.imagemHaila();
    registrarAviso();
  }

  aoIniciarEditor(evento: { editor: any }): void {
    this.quill = evento.editor;
    const toolbar = this.quill?.getModule('toolbar');
    toolbar?.addHandler('video', () => {
      this.posicaoDoVideo = this.quill.getSelection(true)?.index ?? this.quill.getLength();
      this.linkDoVideo = '';
      this.videoAberto = true;
    });
    toolbar?.addHandler('image', () => this.escolherImagem());
  }

  inserirVideo(): void {
    const endereco = enderecoDoVideo(this.linkDoVideo);
    if (!endereco) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Cole o link de um vídeo do YouTube, do Vimeo ou do Loom.' });
      return;
    }
    this.quill?.insertEmbed(this.posicaoDoVideo, 'video', endereco, 'user');
    this.quill?.setSelection(this.posicaoDoVideo + 1, 0, 'user');
    this.videoAberto = false;
  }

  private escolherImagem(): void {
    const posicao = this.quill.getSelection(true)?.index ?? this.quill.getLength();
    const entrada = document.createElement('input');
    entrada.type = 'file';
    entrada.accept = 'image/png,image/jpeg,image/gif,image/webp';
    entrada.onchange = () => {
      const arquivo = entrada.files?.[0];
      if (!arquivo) {
        return;
      }
      if (arquivo.size > 5 * 1024 * 1024) {
        this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'A imagem pode ter no máximo 5 MB.' });
        return;
      }
      this.enviandoImagem = true;
      this.service.enviarImagem(arquivo).subscribe(
        (url) => {
          this.enviandoImagem = false;
          this.quill.insertEmbed(posicao, 'image', url, 'user');
          this.quill.setSelection(posicao + 1, 0, 'user');
        },
        (e) => {
          this.enviandoImagem = false;
          this.erro(e);
        }
      );
    };
    entrada.click();
  }

  ngOnInit(): void {
    this.config.setTranslation({
      dayNames: ['Domingo', 'Segunda-Feira', 'Terça-Feira', 'Quarta-Feira', 'Quinta-Feira', 'Sexta-Feira', 'Sábado'],
      dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
      dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
      monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
      monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
      today: 'Hoje',
      clear: 'Limpar',
    });
    this.carregar();
  }

  carregar(selecionar?: number): void {
    this.carregando = true;
    this.service.posts().subscribe(
      (posts) => {
        this.carregando = false;
        this.posts = posts;
        const alvo = posts.find((p) => p.id === (selecionar ?? this.editando?.id)) || posts[0];
        if (alvo) {
          this.abrir(alvo);
        } else {
          this.novoPost();
        }
      },
      (e) => {
        this.carregando = false;
        this.erro(e);
      }
    );
  }

  get buscando(): boolean {
    return !!this.busca.trim();
  }

  get arvore(): { categoria: CategoriaBlog; posts: PostBlog[] }[] {
    const q = this.busca.trim().toLowerCase();
    return this.categorias
      .map((c) => ({
        categoria: c.value,
        posts: this.posts.filter(
          (p) => p.categoria === c.value && (!q || [p.titulo, p.resumo, p.versao].join(' ').toLowerCase().includes(q))
        ),
      }))
      .filter((g) => !q || g.posts.length);
  }

  get totalNaArvore(): number {
    return this.arvore.reduce((s, g) => s + g.posts.length, 0);
  }

  pastaAberta(categoria: CategoriaBlog): boolean {
    return this.buscando || !this.pastasFechadas.has(categoria);
  }

  alternarPasta(categoria: CategoriaBlog): void {
    if (this.pastasFechadas.has(categoria)) {
      this.pastasFechadas.delete(categoria);
    } else {
      this.pastasFechadas.add(categoria);
    }
  }

  contagem(n: number): string {
    return n === 1 ? '1 post' : `${n} posts`;
  }

  status(p: PostBlog): Status {
    if (p.situacao === 'rascunho') {
      return 'rascunho';
    }
    return new Date(p.publicarEm).getTime() > Date.now() ? 'agendado' : 'publicado';
  }

  rotuloStatus(s: Status): string {
    return { rascunho: 'Rascunho', agendado: 'Agendado', publicado: 'Publicado' }[s];
  }

  categoria(c: CategoriaBlog) {
    return this.categorias.find((x) => x.value === c) || this.categorias[0];
  }

  get comMudancas(): boolean {
    return !!this.form && COM_MUDANCAS.includes(this.form.categoria);
  }

  get dataFutura(): boolean {
    return !!this.form?.publicarEm && this.form.publicarEm.getTime() > Date.now();
  }

  get statusAtual(): Status {
    return this.novo || !this.editando ? 'rascunho' : this.status(this.editando);
  }

  get alterado(): boolean {
    return !!this.form && JSON.stringify(this.form) !== this.original;
  }

  selecionar(p: PostBlog): void {
    if (p.id === this.editando?.id && !this.novo) {
      return;
    }
    this.descartarSePreciso(() => this.abrir(p));
  }

  novoPost(): void {
    this.descartarSePreciso(() => {
      this.editando = null;
      this.novo = true;
      this.preencher({
        titulo: '',
        categoria: 'novidade',
        versao: '',
        resumo: '',
        texto: '',
        mudancas: {},
        destaque: false,
        situacao: 'rascunho',
        publicarEm: new Date().toISOString(),
      });
    });
  }

  salvar(situacao: 'rascunho' | 'publicado'): void {
    const f = this.form;
    if (!f.titulo.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Dê um título ao post antes de salvar.' });
      return;
    }
    const linhas = (t: string) => t.split('\n').map((s) => s.trim()).filter(Boolean);
    const mudancas: Partial<Record<GrupoMudanca, string[]>> = {};
    if (this.comMudancas) {
      this.grupos.forEach((g) => {
        const itens = linhas(f[g.chave]);
        if (itens.length) {
          mudancas[g.chave] = itens;
        }
      });
    }
    const post: PostBlog = {
      titulo: f.titulo.trim(),
      categoria: f.categoria,
      versao: this.comMudancas ? f.versao.trim() : '',
      resumo: f.resumo.trim(),
      texto: textoVazio(f.texto) ? '' : f.texto.trim(),
      mudancas,
      destaque: f.destaque,
      situacao,
      publicarEm: (f.publicarEm || new Date()).toISOString(),
      enquete: f.enquetePergunta.trim() || linhas(f.enqueteOpcoes).length
        ? { pergunta: f.enquetePergunta.trim(), opcoes: linhas(f.enqueteOpcoes).map((texto) => ({ texto })) }
        : null,
    };
    this.salvando = true;
    const req = this.novo ? this.service.incluir(post) : this.service.alterar(this.editando.id, post);
    req.subscribe(
      (salvo) => {
        this.salvando = false;
        const i = this.posts.findIndex((p) => p.id === salvo.id);
        if (i >= 0) {
          this.posts[i] = salvo;
        } else {
          this.posts.unshift(salvo);
        }
        this.posts.sort((a, b) => b.publicarEm.localeCompare(a.publicarEm) || b.id - a.id);
        this.posts = [...this.posts];
        this.abrir(salvo);
        const s = this.status(salvo);
        const detalhe =
          s === 'publicado'
            ? 'Publicado! Os clientes já veem este post.'
            : s === 'agendado'
            ? 'Agendado para ' + this.formatar(salvo.publicarEm) + '.'
            : 'Rascunho salvo.';
        this.messageService.add({ severity: 'success', summary: 'Pronto', detail: detalhe });
      },
      (e) => {
        this.salvando = false;
        this.erro(e);
      }
    );
  }

  excluir(): void {
    const post = this.editando;
    this.confirmationService.confirm({
      message: `Excluir o post "${post.titulo}"? Ele sai do News na hora e não dá para desfazer.`,
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.service.excluir(post.id).subscribe(
          () => {
            this.posts = this.posts.filter((p) => p.id !== post.id);
                this.editando = null;
            this.form = null;
            this.original = '';
            if (this.posts.length) {
              this.abrir(this.posts[0]);
            } else {
              this.novoPost();
            }
            this.messageService.add({ severity: 'success', summary: 'Pronto', detail: 'Post excluído.' });
          },
          (e) => this.erro(e)
        );
      },
    });
  }

  abrirBlog(id?: number): void {
    window.open(this.service.linkPost(id), '_blank');
  }

  get totalVotosEnquete(): number {
    return (this.editando?.enquete?.opcoes || []).reduce((s, o) => s + (o.votos || 0), 0);
  }

  get totalReacoes(): number {
    return Object.values(this.editando?.reacoes || {}).reduce((s, n) => s + (n || 0), 0);
  }

  reacao(chave: string): number {
    return (this.editando?.reacoes || {})[chave] || 0;
  }

  percentual(votos: number): number {
    return this.totalVotosEnquete ? Math.round(((votos || 0) * 100) / this.totalVotosEnquete) : 0;
  }

  formatar(iso: string): string {
    const d = new Date(iso);
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${dois(d.getDate())}/${dois(d.getMonth() + 1)}/${d.getFullYear()} às ${dois(d.getHours())}h${dois(d.getMinutes())}`;
  }

  private abrir(p: PostBlog): void {
    this.editando = p;
    this.novo = false;
    this.preencher(p);
  }

  private preencher(p: PostBlog): void {
    this.form = {
      titulo: p.titulo,
      categoria: p.categoria,
      versao: p.versao || '',
      publicarEm: new Date(p.publicarEm),
      resumo: p.resumo || '',
      texto: textoParaHtml(p.texto || ''),
      novo: (p.mudancas?.novo || []).join('\n'),
      melhoria: (p.mudancas?.melhoria || []).join('\n'),
      correcao: (p.mudancas?.correcao || []).join('\n'),
      destaque: p.destaque,
      enquetePergunta: p.enquete?.pergunta || '',
      enqueteOpcoes: (p.enquete?.opcoes || []).map((o) => o.texto).join('\n'),
    };
    this.original = JSON.stringify(this.form);
  }

  private descartarSePreciso(seguir: () => void): void {
    if (!this.alterado) {
      seguir();
      return;
    }
    this.confirmationService.confirm({
      message: 'Você tem alterações que ainda não foram salvas. Descartar e continuar?',
      acceptLabel: 'Descartar',
      rejectLabel: 'Continuar editando',
      accept: seguir,
    });
  }

  private erro(e: HttpErrorResponse): void {
    this.messageService.add({ severity: 'error', summary: 'Erro', detail: this.service.mensagemErro(e), life: 8000 });
  }
}
